import React, { useState, useContext, useEffect, useCallback } from "react";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  Box,
  IconButton,
  useTheme,
  Drawer,
  Typography,
  Button,
  Divider,
  Stack,
  Menu,
  MenuItem,
  Avatar,
  Snackbar,
  Alert,
} from "@mui/material";
import { ReactSession } from "react-client-session";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Icons
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { ExitToApp } from "@mui/icons-material";

// Theme
import { tokens, ColorModeContext, useMode } from "./theme";

// Components
import Dashboard from "../pages/Dashboard";
import Welcome from "../pages/Welcome";
import Login from "../pages/Login";
import Profiles from "../pages/Profiles";
import User from "../pages/User";
import Client from "../pages/Client";
import Fournisseur from "../pages/Fournisseur";
import Produit from "../pages/Produit";
import ChangePassword from "../pages/ChangePassword";
import { usePermissions } from "../pages/hooks/usePermissions";
import PermissionsPage from "../pages/PermissionsPage";
import Permissions from "../pages/Permissions";
import Roles from "../pages/Roles";
import Tiers from "../pages/Tiers";
import SalesManagement from "../pages/SalesManagement";
import AchatManagement from "../pages/AchatManagement";
import RefundManagement from "../pages/RefundManagement";
import Invoice from "../pages/PrintPageTemplate";
import UserProfile from "../pages/UserProfile";
import PendingPaymentsConfirmation from "../pages/PendingPaymentsConfirmation";
import InstallmentListPage from "../pages/InstallmentDetailsPage";
import FacturesManagement from "../pages/FactureManagement";
import Categories from "../pages/Categories";

function ToolbarActions() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inactivityTimeout, setInactivityTimeout] = useState(120 * 60 * 1000);
  const [tempTimeout, setTempTimeout] = useState(30);
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentMode, setCurrentMode] = useState(
    document.documentElement.getAttribute("data-toolpad-color-scheme") ||
      "light"
  );
  const [anchorEl, setAnchorEl] = useState(null);

  const username = ReactSession.get("username");
  const role = ReactSession.get("role");

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  // Update currentMode when theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "data-toolpad-color-scheme") {
          setCurrentMode(
            document.documentElement.getAttribute(
              "data-toolpad-color-scheme"
            ) || "light"
          );
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-toolpad-color-scheme"],
    });

    return () => observer.disconnect();
  }, []);

  const toggleTheme = () => {
    const root = document.documentElement;
    const newMode = currentMode === "dark" ? "light" : "dark";
    root.setAttribute("data-toolpad-color-scheme", newMode);
    setCurrentMode(newMode);
  };

  const handleLogout = useCallback(() => {
    ReactSession.set("username", "");
    ReactSession.set("role", "");
    navigate("/login", { replace: true });
  }, [navigate]);

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <IconButton
        onClick={toggleDrawer(true)}
        sx={{
          color: currentMode === "light" ? colors.primary[100] : colors.primary[900],
        }}
      >
        <SettingsOutlinedIcon />
      </IconButton>

      {/* Profiles Menu */}
      <Box
        sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        onClick={handleProfileClick}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: colors.blueAccent[500],
            color: colors.primary[900],
            fontSize: "1rem",
            marginRight: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: colors.primary[900],
              fontSize: "2rem",
              fontWeight: "bold",
            }}
          >
            {username ? username.charAt(0).toUpperCase() : "U"}
          </Typography>
        </Avatar>
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: colors.primary[100],
            }}
          >
            {username || "Utilisateur"}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: colors.primary[200],
            }}
          >
            {role || "Rôle"}
          </Typography>
        </Box>
      </Box>
      {/* <Box display="flex" justifyContent="center" mb={2}>
        <IconButton
          onClick={() => colorMode.toggleColorMode()}
          sx={{
            color: colors.grey[100],
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.blueAccent[700]
                : colors.blueAccent[800],
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? colors.blueAccent[800]
                  : colors.primary[300],
            },
          }}
        >
          {theme.palette.mode === "dark" ? (
            <LightModeOutlinedIcon />
          ) : (
            <DarkModeOutlinedIcon />
          )}
        </IconButton>
      </Box> */}
      <Box display="flex" justifyContent="center" mb={2}>
        <IconButton
          onClick={() => colorMode.toggleColorMode()}
          sx={{
            color: colors.primary[100],
            backgroundColor: colors.secondary,
            "&:hover": {
              backgroundColor: colors.secondary,
            },
          }}
        >
          {theme.palette.mode === "dark" ? (
            <LightModeOutlinedIcon />
          ) : (
            <DarkModeOutlinedIcon />
          )}
        </IconButton>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileClose}
        onClick={handleProfileClose}
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[900],
            color: colors.primary[100],
            mt: 1.5,
            "& .MuiMenuItem-root": {
              typography: "body2",
              fontSize: "0.875rem",
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => navigate("/user_profile")}>
          Mon Profil
        </MenuItem>
        <MenuItem onClick={handleLogout}>Déconnexion</MenuItem>
      </Menu>

      {/* Settings Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: colors.primary[900],
            color: colors.grey[100],
          },
        }}
      >
        <Box
          role="presentation"
          p={2}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={toggleDrawer(false)}
          width={300}
        >
          <Typography
            variant="h4"
            fontWeight="bold"
            mb={2}
          >
            Paramètres
          </Typography>
          <Divider sx={{ borderColor: colors.grey[500], my: 2 }} />
        </Box>
      </Drawer>
    </Stack>
  );
}

const DashboardLayoutBasic = ({ loggedIn, onLogin }) => {
  const [theme, colorMode] = useMode();
  const userRole = ReactSession.get("role");
  const { hasPermission, loading, permissions } = usePermissions();
  const [inactivityTimeout, setInactivityTimeout] = useState(120 * 60 * 1000);
  const [countdown, setCountdown] = useState(60);
  const [showExpirationAlert, setShowExpirationAlert] = useState(false);
  const navigate = useNavigate();

  // Check token expiration
  useEffect(() => {
    let countdownInterval;
    if (showExpirationAlert && countdown > 0) {
      countdownInterval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(countdownInterval);
  }, [showExpirationAlert, countdown]);

  useEffect(() => {
    const token = ReactSession.get("token");

    const checkTokenExpiration = () => {
      if (!token) return;

      try {
        // Decode the token to get expiration time (assuming JWT token)
        const tokenData = JSON.parse(atob(token.split(".")[1]));
        const expirationTime = tokenData.exp * 1000; // Convert to milliseconds
        const currentTime = new Date().getTime();
        const timeToExpire = expirationTime - currentTime;

        // Show warning when 60 seconds remaining
        if (timeToExpire > 0 && timeToExpire <= 60 * 1000) {
          setShowExpirationAlert(true);
          setCountdown(Math.floor(timeToExpire / 1000));
        }
        // Immediate logout if expired
        else if (timeToExpire <= 0) {
          handleLogout();
        }
      } catch (error) {
        console.error("Error checking token expiration:", error);
      }
    };

    // Check every minute
    const intervalId = setInterval(checkTokenExpiration, 60 * 1000);

    // Initial check
    checkTokenExpiration();

    return () => clearInterval(intervalId);
  }, []);

  const handleLogout = useCallback(() => {
    ReactSession.set("token", "");
    ReactSession.set("username", "");
    ReactSession.set("role", "");
    ReactSession.set("idUser", "");
    ReactSession.set("profile", "");
    navigate("/login", { replace: true });
  }, [navigate]);

  // useEffect(() => {
  //   const handleOnline = () => {
  //     console.log("Back online");
  //   };

  //   const handleOffline = () => {
  //     console.log("Gone offline");
  //     handleLogout();
  //   };

  //   window.addEventListener("online", handleOnline);
  //   window.addEventListener("offline", handleOffline);

  //   if (!navigator.onLine) {
  //     handleLogout();
  //   }

  //   return () => {
  //     window.removeEventListener("online", handleOnline);
  //     window.removeEventListener("offline", handleOffline);
  //   };
  // }, [handleLogout]);

  // useEffect(() => {
  //   let inactivityTimer;

  //   const resetInactivityTimer = () => {
  //     clearTimeout(inactivityTimer);
  //     inactivityTimer = setTimeout(handleLogout, inactivityTimeout);
  //   };

  //   const activityEvents = ["mousedown", "keydown", "touchstart", "scroll"];
  //   activityEvents.forEach((event) => {
  //     window.addEventListener(event, resetInactivityTimer);
  //   });

  //   resetInactivityTimer();

  //   return () => {
  //     activityEvents.forEach((event) => {
  //       window.removeEventListener(event, resetInactivityTimer);
  //     });
  //     clearTimeout(inactivityTimer);
  //   };
  // }, [handleLogout, inactivityTimeout]);

  // Handle non-logged in state
  if (!loggedIn) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={onLogin} />} />
        <Route path="/*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Show loading state while permissions are being fetched
  if (loading) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 9999,
        }}
      >
        <Typography variant="h6" color="white">
          Chargement...
        </Typography>
      </Box>
    );
  }

  const availableRoutes = [
    // General routes
    {
      path: "/dashboard",
      element: <Dashboard />,
      requiredPermission: "dashboard",
      allowedRoles: ["user"],
    },
    {
      path: "/welcome",
      element: <Welcome />,
      requiredPermission: "welcome",
    },

    // Gestion des Ventes routes
    {
      path: "/ventes",
      element: <SalesManagement />,
      requiredPermission: "vente",
    },
    {
      path: "/ventes/client",
      element: <Client />,
      requiredPermission: "client",
    },
    {
      path: "/ventes/vente",
      element: <SalesManagement />,
      requiredPermission: "vente",
    },
    {
      path: "/ventes/confirmation",
      element: <PendingPaymentsConfirmation />,
      requiredPermission: "confirmation",
    },
    {
      path: "/ventes/installment-details",
      element: <InstallmentListPage />,
      requiredPermission: "installment-details",
    },
    {
      path: "/ventes/refund",
      element: <RefundManagement />,
      requiredPermission: "refund",
    },
    {
      path: "/ventes/facture",
      element: <FacturesManagement />,
      requiredPermission: "facture",
    },

    // Gestion des Achats routes
    {
      path: "/achats",
      element: <AchatManagement />,
      requiredPermission: "achat",
    },
    {
      path: "/achats/fournisseur",
      element: <Fournisseur />,
      requiredPermission: "fournisseur",
    },
    {
      path: "/achats/achat",
      element: <AchatManagement />,
      requiredPermission: "achat",
    },

    // Inventaire routes
    {
      path: "/inventaire",
      element: <Produit />,
      requiredPermission: "article",
    },
    {
      path: "/inventaire/article",
      element: <Produit />,
      requiredPermission: "article",
    },

    // Utilisateurs et Configuration routes
    {
      path: "/users-config",
      element: <User />,
      requiredPermission: "utilisateur",
    },
    {
      path: "/users-config/utilisateur",
      element: <User />,
      requiredPermission: "utilisateur",
    },
    {
      path: "/users-config/profile",
      element: <Profiles />,
      requiredPermission: "profile",
    },
    {
      path: "/users-config/permissions",
      element: <Permissions />,
      requiredPermission: "permissions",
      allowedRoles: ["admin"],
    },
    {
      path: "/users-config/roles",
      element: <Roles />,
      requiredPermission: "roles",
    },
    {
      path: "/users-config/tiers",
      element: <Tiers />,
      requiredPermission: "tiers",
    },
    {
      path: "/users-config/categories",
      element: <Categories />,
      requiredPermission: "categories",
    },

    // Additional routes
    {
      path: "/user_profile",
      element: <UserProfile />,
      requiredPermission: "user_profile",
    },
  ];

  const authorizedRoutes = availableRoutes.filter((route) => {
    if (userRole === "admin") {
      return true;
    }
    if (route.allowedRoles && !route.allowedRoles.includes(userRole)) {
      return false;
    }
    return hasPermission(route.requiredPermission);
  });

  const getDefaultRoute = () => {
    if (userRole === "admin") {
      return <Dashboard />;
    }
    if (hasPermission("welcome")) {
      return <Welcome />;
    }
    if (authorizedRoutes.length > 0) {
      const FirstAuthorizedComponent = authorizedRoutes[0].element;
      return FirstAuthorizedComponent;
    }
    return <div>Aucune page autorisée disponible</div>;
  };

  const handleStayLoggedIn = () => {
    setShowExpirationAlert(false);
    setCountdown(60);
  };

  return (
    <>
      <DashboardLayout
        slots={{
          toolbarActions: ToolbarActions,
        }}
        slotProps={{
          toolbar: {
            hideThemeSwitch: true,
            disableThemeSwitch: true,
          },
        }}
      >
        <Box sx={{ flexGrow: 1, p: 2, overflowY: "auto" }}>
          <Routes>
            <Route path="/" element={getDefaultRoute()} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            {authorizedRoutes.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </DashboardLayout>

      {showExpirationAlert && (
        <Snackbar
          open={showExpirationAlert}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="warning"
            variant="filled"
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button color="inherit" size="small" onClick={handleLogout}>
                  Se déconnecter maintenant
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleStayLoggedIn}
                >
                  Rester connecté
                </Button>
              </Box>
            }
          >
            Votre session expirera dans {countdown} secondes
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default DashboardLayoutBasic;
